// src/App.js
import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import AdminSlides      from './components/admin/AdminSlides';
import AdminDiscounts   from './components/admin/AdminDiscounts';
import AdminWheel       from './components/admin/AdminWheel';
import AdminCompliance  from './components/admin/AdminCompliance';
import WheelScreen      from './pages/WheelScreen';
import { AppProvider, useApp } from './lib/AppContext';
import { useSEO, SITE, websiteSchema, organizationSchema } from './lib/useSEO';
import './styles/global.css';

import AgeGate      from './components/shared/AgeGate';
import TopNav       from './components/shared/TopNav';
import CategoryNav  from './components/shared/CategoryNav';
import BottomNav    from './components/shared/BottomNav';
import SiteFooter   from './components/shared/SiteFooter';
import AdminSidebar from './components/admin/AdminSidebar';
import CartSheet    from './components/customer/CartSheet';
import ToastStack   from './components/shared/ToastStack';

import HomeScreen     from './pages/HomeScreen';
import ProductScreen  from './pages/ProductScreen';
import CheckoutScreen from './pages/CheckoutScreen';
import OrderSuccess   from './pages/OrderSuccess';
import SearchScreen   from './pages/SearchScreen';
import BlogScreen     from './pages/BlogScreen';
import BlogPostScreen from './pages/BlogPostScreen';
import LoginScreen    from './pages/LoginScreen';
import SignupScreen   from './pages/SignupScreen';
import AccountScreen  from './pages/AccountScreen';
import OrdersScreen   from './pages/OrdersScreen';
import WishlistScreen from './pages/WishlistScreen';
import LoyaltyScreen  from './pages/LoyaltyScreen';
import SettingsScreen from './pages/SettingsScreen';
import DebugScreen    from './pages/DebugScreen';
import ContactPage    from './pages/ContactPage';
import TermsPage      from './pages/legal/TermsPage';
import PrivacyPage    from './pages/legal/PrivacyPage';
import CookiesPage    from './pages/legal/CookiesPage';
import ShippingPage   from './pages/legal/ShippingPage';
import DisclaimerPage from './pages/legal/DisclaimerPage';

import AdminDashboard   from './components/admin/AdminDashboard';
import AdminProducts    from './components/admin/AdminProducts';
import AdminProductEdit from './components/admin/AdminProductEdit';
import AdminOrders      from './components/admin/AdminOrders';
import AdminBlog        from './components/admin/AdminBlog';
import AdminBlogEdit    from './components/admin/AdminBlogEdit';
import AdminNewsletter  from './components/admin/AdminNewsletter';
import AdminCustomers   from './components/admin/AdminCustomers';
import AdminAnalytics   from './components/admin/AdminAnalytics';

// ── Inject <link rel="preconnect"> hints once on startup ─────────────────────
function GlobalHead() {
  useEffect(() => {
    const hints = [
      { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
      { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossorigin: '' },
      // Replace with your actual Supabase project URL:
      { rel: 'dns-prefetch', href: process.env.REACT_APP_SUPABASE_URL || '' },
    ];
    hints.forEach(({ rel, href, crossorigin }) => {
      if (!href) return;
      if (document.querySelector(`link[rel="${rel}"][href="${href}"]`)) return;
      const el = document.createElement('link');
      el.rel  = rel;
      el.href = href;
      if (crossorigin !== undefined) el.crossOrigin = '';
      document.head.appendChild(el);
    });

    // Viewport meta — ensure it's set correctly for ecommerce mobile
    let viewport = document.querySelector('meta[name="viewport"]');
    if (!viewport) {
      viewport = document.createElement('meta');
      viewport.name = 'viewport';
      document.head.appendChild(viewport);
    }
    viewport.content = 'width=device-width, initial-scale=1, viewport-fit=cover';

    // Theme color for mobile chrome
    let tc = document.querySelector('meta[name="theme-color"]');
    if (!tc) { tc = document.createElement('meta'); tc.name = 'theme-color'; document.head.appendChild(tc); }
    tc.content = '#0a0a0a';
  }, []);
  return null;
}

// ── Admin routes get noIndex ──────────────────────────────────────────────────
function AdminSEO() {
  useSEO({ title: 'Admin', noIndex: true, lang: 'en' });
  return null;
}

// ── Utility page SEO (order-success, debug, etc.) ────────────────────────────
function UtilitySEO({ title }) {
  useSEO({ title, noIndex: true, lang: 'en' });
  return null;
}

function FullScreenSpinner() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0a0a0a' }}>
      <div className="spinner" />
    </div>
  );
}

function AppShell() {
  const { ageVerified, authLoading, cartOpen, user, isAdmin, profile } = useApp();
  const { pathname } = useLocation();
  const isAdminRoute = pathname.startsWith('/admin');

  // Wait for auth to resolve before showing anything
  if (authLoading) return <FullScreenSpinner />;

  // Age gate always first
  if (!ageVerified) return <AgeGate />;

  // ── ADMIN LAYOUT ─────────────────────────────────────────────────────────
  if (isAdminRoute) {
    // Not logged in at all
    if (!user) return <Navigate to="/login" replace />;

    // Profile is still loading (null but user exists) — show spinner briefly
    if (!profile) return <FullScreenSpinner />;

    // Logged in but not admin — show a clear message instead of silent redirect
    if (!isAdmin) {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#0a0a0a', padding: '2rem', textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔒</div>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '1.6rem', color: '#f4f4f4', marginBottom: 8 }}>Admin Access Required</div>
          <p style={{ color: '#666', fontSize: '.88rem', maxWidth: 360, lineHeight: 1.8, marginBottom: 24 }}>
            Your account ({profile?.email}) has role: <strong style={{ color: '#f0a500' }}>{profile?.role || 'unknown'}</strong>.<br />
            Run this SQL in Supabase, then visit <a href="/debug" style={{ color: '#4cca74' }}>/debug</a> and click Refresh Profile.
          </p>
          <code style={{ background: '#111', border: '1px solid #333', borderRadius: 8, padding: '10px 16px', fontSize: '.82rem', color: '#4cca74', fontFamily: 'monospace', marginBottom: 24, display: 'block' }}>
            UPDATE public.profiles SET role = 'admin'<br />
            WHERE email = '{profile?.email}';
          </code>
          <div style={{ display: 'flex', gap: 12 }}>
            <a href="/debug" style={{ background: '#4cca74', border: 'none', borderRadius: 25, padding: '10px 24px', color: '#000', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '.85rem', fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', textDecoration: 'none' }}>
              Open Debug →
            </a>
            <a href="/" style={{ background: 'none', border: '1px solid #333', borderRadius: 25, padding: '10px 24px', color: '#888', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '.85rem', fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', textDecoration: 'none' }}>
              Back to Store
            </a>
          </div>
        </div>
      );
    }

    // Admin confirmed — render admin layout
    return (
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <AdminSEO />
        <TopNav />
        <div style={{ display: 'flex', flex: 1, position: 'relative' }}>
          <AdminSidebar />
          <div className="admin-content-area" style={{ flex: 1, overflowX: 'hidden', background: '#0a0a0a', minWidth: 0 }}>
            <Routes>
              <Route path="/admin"                element={<AdminDashboard />} />
              <Route path="/admin/products"       element={<AdminProducts />} />
              <Route path="/admin/products/new"   element={<AdminProductEdit />} />
              <Route path="/admin/products/:id"   element={<AdminProductEdit />} />
              <Route path="/admin/orders"         element={<AdminOrders />} />
              <Route path="/admin/blog"           element={<AdminBlog />} />
              <Route path="/admin/blog/new"       element={<AdminBlogEdit />} />
              <Route path="/admin/blog/:id"       element={<AdminBlogEdit />} />
              <Route path="/admin/newsletter"     element={<AdminNewsletter />} />
              <Route path="/admin/customers"      element={<AdminCustomers />} />
              <Route path="/admin/analytics"      element={<AdminAnalytics />} />
              <Route path="/admin/slides"      element={<AdminSlides />} />
              <Route path="/admin/discounts"  element={<AdminDiscounts />} />
              <Route path="/admin/wheel"      element={<AdminWheel />} />
              <Route path="/admin/compliance" element={<AdminCompliance />} />
              <Route path="*"                     element={<Navigate to="/admin" replace />} />
            </Routes>
          </div>
        </div>
        <ToastStack />
      </div>
    );
  }

  // ── CUSTOMER LAYOUT ───────────────────────────────────────────────────────
  const showCategoryNav = pathname === '/' || pathname === '/search';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <GlobalHead />
      <TopNav />
      {showCategoryNav && <CategoryNav />}
      <main style={{ flex: 1 }}>
        <Routes>
          <Route path="/"              element={<HomeScreen />} />
          <Route path="/product/:id"   element={<ProductScreen />} />
          <Route path="/search"        element={<SearchScreen />} />
          <Route path="/blog"          element={<BlogScreen />} />
          <Route path="/blog/:slug"    element={<BlogPostScreen />} />
          <Route path="/loyalty"       element={<LoyaltyScreen />} />
          <Route path="/wheel"         element={<WheelScreen />} />
          <Route path="/debug"         element={<DebugScreen />} />
          <Route path="/login"         element={user ? <Navigate to="/account" replace /> : <LoginScreen />} />
          <Route path="/signup"        element={user ? <Navigate to="/account" replace /> : <SignupScreen />} />
          <Route path="/order-success" element={<><UtilitySEO title="Order Confirmed" /><OrderSuccess /></>} />
          <Route path="/checkout"      element={user ? <><UtilitySEO title="Checkout" /><CheckoutScreen /></>   : <Navigate to="/login" replace />} />
          <Route path="/account"       element={user ? <AccountScreen />    : <Navigate to="/login" replace />} />
          <Route path="/orders"        element={user ? <OrdersScreen />     : <Navigate to="/login" replace />} />
          <Route path="/wishlist"      element={user ? <WishlistScreen />   : <Navigate to="/login" replace />} />
          <Route path="/settings"      element={user ? <><UtilitySEO title="Settings" /><SettingsScreen /></>   : <Navigate to="/login" replace />} />
          <Route path="/contact"       element={<ContactPage />} />
          <Route path="/legal/terms"       element={<TermsPage />} />
          <Route path="/legal/privacy"     element={<PrivacyPage />} />
          <Route path="/legal/cookies"     element={<CookiesPage />} />
          <Route path="/legal/shipping"    element={<ShippingPage />} />
          <Route path="/legal/disclaimer"  element={<DisclaimerPage />} />
          {/* Legacy flat paths — redirect to /legal/* */}
          <Route path="/terms"      element={<Navigate to="/legal/terms"      replace />} />
          <Route path="/privacy"    element={<Navigate to="/legal/privacy"    replace />} />
          <Route path="/cookies"    element={<Navigate to="/legal/cookies"    replace />} />
          <Route path="/shipping"   element={<Navigate to="/legal/shipping"   replace />} />
          <Route path="/disclaimer" element={<Navigate to="/legal/disclaimer" replace />} />
          <Route path="*"              element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <SiteFooter />
      <BottomNav />
      {cartOpen && <CartSheet />}
      <ToastStack />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppProvider>
        <AppShell />
      </AppProvider>
    </BrowserRouter>
  );
}